import { useMutation } from '@tanstack/react-query';
import { splitEvery } from 'ramda';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { getErrorContent } from 'common/utils';
import { offeringApiUpdateOffers } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import {
  OfferWithSelectionAndAuthor,
  OfferingApiError,
  OfferingApiUpdateOffersPayload,
} from 'modules/shared-menu/types';

import { buildBatchUpdatePayload } from './transformers';

type ExecuteBatchOffersArgs = {
  offerPayloads: OfferWithSelectionAndAuthor[];
  onSuccess: (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => void;
  onError: (updateErrors: OfferingApiError[]) => void;
};

const OFFER_CHUNK_SIZE = 100;

export const useBatchUpdateOffers = () => {
  const scope = useScopeContext();
  const intl = useIntl();

  const { mutateAsync, isLoading } = useMutation(async (payload: OfferingApiUpdateOffersPayload) =>
    offeringApiUpdateOffers(payload),
  );

  const executeBatchUpdateOffers = useCallback(
    async ({ offerPayloads, onSuccess, onError }: ExecuteBatchOffersArgs) => {
      const offersChunksArray = splitEvery(OFFER_CHUNK_SIZE, offerPayloads);
      const successfullyUpdatedOffers: OfferWithSelectionAndAuthor[] = [];
      const updateErrors: OfferingApiError[] = [];

      for (const offersChunk of offersChunksArray) {
        const payload = buildBatchUpdatePayload(offersChunk, scope);

        try {
          const response = await mutateAsync(payload);
          successfullyUpdatedOffers.push(...response.offers);
          if (!!response.errors && response.errors.length > 0) {
            updateErrors.push(...response.errors);
          }
        } catch (e) {
          const offerErrors: OfferingApiError[] = offersChunk.map(({ id }) => ({
            id,
            code: '',
            message: getErrorContent(e) || intl.formatMessage({ id: 'errors.unknown-error-occurred' }),
          }));
          updateErrors.push(...offerErrors);
        }
      }

      if (successfullyUpdatedOffers.length) {
        onSuccess(successfullyUpdatedOffers);
      }

      if (updateErrors.length) {
        onError(updateErrors);
      }
    },
    [intl, mutateAsync, scope],
  );

  return { executeBatchUpdateOffers, isLoading };
};
